import type { AnalyticsDestination, AnalyticsTraits, EventProperties } from "@/lib/analytics/analytics.types";

/**
 * Object-style data-layer event name for GTM Custom Event triggers.
 *
 * Must be a plain object (`{ event: "signup_complete" }`), not a gtag
 * Arguments push (`gtag("event", "signup_complete")`). GTM Custom Event
 * triggers only match object-style entries.
 */
export const GTM_SIGNUP_COMPLETE_EVENT = "signup_complete";

const enabled = (): boolean => typeof window !== "undefined" && process.env.NODE_ENV !== "test";

/**
 * Queue a GTM custom event. Seeds `window.dataLayer` so the push is retained
 * if gtm.js has not loaded yet (same queue GTM's snippet uses).
 *
 * Not gated on NEXT_PUBLIC_GTM_ID: the payload is the conversion signal;
 * the container loader is separate. Empty-ID environments still queue, so
 * tests can assert the object-style entry without a live container.
 */
const pushSignupComplete = (): void => {
  if (!enabled()) {
    return;
  }
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event: GTM_SIGNUP_COMPLETE_EVENT });
};

/**
 * GTM data-layer destination. Does not replace the GA4 gtag facade — it only
 * emits `signup_complete` when the shared one-shot `sign_up` track fires
 * (marker + winning ack + non-admin). Page views and identity stay with GA4.
 */
export const gtmDestination: AnalyticsDestination = {
  name: "gtm",
  isEnabled: enabled,
  pageView: () => {
    // SPA page views stay on the GA4 facade so a GTM GA4 tag is not needed.
  },
  track: (name, _properties?: EventProperties) => {
    if (name !== "sign_up") {
      return;
    }
    pushSignupComplete();
  },
  identify: (_traits: AnalyticsTraits) => {
    // Enhanced-conversions email is set by ga4Destination via gtag user_data.
  },
  reset: () => {
    // Session/cookie lifecycle stays with gtag / GTM container.
  },
};
